import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../utils/logger';
import PageHeader from '../common/PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSave, faSpinner, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import AvatarEditor from './AvatarEditor';
import PhotoUpload from '../common/PhotoUpload';
import ConfirmDialog from '../common/ConfirmDialog'; // Added
import { uploadAvatar, uploadProfilePhoto, deleteFileByUrl } from '../../services/storageService';
import { ensureUserRole, requestDeleteUserAccount, validateUsername, updateUsername } from '../../services/userService'; // Added validateUsername and updateUsername
import { GenderOption, SexualityOption } from '../../types/user';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import '../../styles/Auth.css';
import '../../styles/UserProfile.css';

/**
 * Enhanced user profile component with editable fields and account deletion
 */
const UserProfile: React.FC = () => {  
  const { 
    currentUser, 
    userProfile, 
    loading, 
    profileLoading, 
    hasUsername,
    verifyEmail, 
    isEmailVerified, 
    updateUserProfile,
    updateExtendedProfile,
    watchEmailVerificationStatus,
    signOut
  } = useAuth();
  
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [verificationSent, setVerificationSent] = useState<boolean>(false);
  const [verificationCountdown, setVerificationCountdown] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showAvatarEditor, setShowAvatarEditor] = useState<boolean>(false);
  // Initialize analytics hooks
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking
  
  // Form fields
  const [introduction, setIntroduction] = useState<string>('');
  const [consentUnderstanding, setConsentUnderstanding] = useState<string>('');
  const [interests, setInterests] = useState<string[]>([]);
  const [interestInput, setInterestInput] = useState<string>('');
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState<string | null>(null);
  const [gender, setGender] = useState<GenderOption | ''>('');
  const [genderOther, setGenderOther] = useState<string>('');  const [sexuality, setSexuality] = useState<SexualityOption | ''>('');
  const [sexualityOther, setSexualityOther] = useState<string>('');  const [birthDate, setBirthDate] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  
  // Track original values to detect changes
  const [originalIntroduction, setOriginalIntroduction] = useState<string>('');
  const [originalConsentUnderstanding, setOriginalConsentUnderstanding] = useState<string>('');
  const [originalInterests, setOriginalInterests] = useState<string[]>([]);
  const [originalPhotoUrls, setOriginalPhotoUrls] = useState<string[]>([]);
  const [originalGender, setOriginalGender] = useState<GenderOption | ''>('');
  const [originalGenderOther, setOriginalGenderOther] = useState<string>('');  const [originalSexuality, setOriginalSexuality] = useState<SexualityOption | ''>('');
  const [originalSexualityOther, setOriginalSexualityOther] = useState<string>('');
  const [originalBirthDate, setOriginalBirthDate] = useState<string>('');
  const [originalUsername, setOriginalUsername] = useState<string>('');
  
  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  
  // Gender options array
  const genderOptions: GenderOption[] = [
    'Man',
    'Woman',
    'Agender',
    'Androgynous',
    'Bigender',
    'Gender Fluid',
    'Gender Nonconforming',
    'Genderqueer',
    'Gender questioning',
    'Intersex',
    'Non-binary',
    'Other',
    'Pangender',
    'Transfeminine',
    'Transmasculine',
    'Trans person',
    'Trans Man',
    'Trans Non-binary',
    'Trans Woman',
    'Two-Spirit',
  ];

  // Sexuality options array
  const sexualityOptions: SexualityOption[] = [
    'Straight',
    'Androgynosexual',
    'Androsexual',
    'Asexual',
    'Autosexual',
    'Bisexual',
    'Bi-curious',
    'Demisexual',
    'Gay',
    'GrayA',
    'Gynosexual',
    'Heteroflexible',
    'Homoflexible',
    'Lesbian',
    'Objectumsexual',
    'Omnisexual',
    'Pansexual',
    'Polysexual',
    'Queer',
    'Skoliosexual',
    'Heterosexual',
    'Questioning',
    'Other',
  ];

  // State for account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false); // Added
  const [deleteError, setDeleteError] = useState<string>(''); // Added
  const [isDeleting, setIsDeleting] = useState<boolean>(false); // Added
  
  const navigate = useNavigate();

  // Calculate profile completeness percentage using useCallback to prevent re-creation on every render
  const calculateProfileCompleteness = useCallback((): number => {
    if (!userProfile) return 0;
    
    let completedFields = 0;
    let totalFields = 7; // Introduction, consent understanding, interests, gender, sexuality, birth date, photos
    
    if (userProfile.introduction) completedFields++;
    if (userProfile.consentUnderstanding) completedFields++; // Count consent understanding field
    if (userProfile.interests && userProfile.interests.length > 0) completedFields++;
    if (userProfile.gender) completedFields++;
    if (userProfile.sexuality) completedFields++;
    if (userProfile.birthDate) completedFields++;
    if (userProfile.photoUrls && userProfile.photoUrls.length > 0) completedFields++;
    
    return Math.round((completedFields / totalFields) * 100);
  }, [userProfile]);

  // Track page view when component mounts
  useEffect(() => {
    trackPageView({ 
      page_title: 'User Profile',
      page_location: window.location.href 
    });
    
    // Track user profile view with user properties
    trackCustomEvent('profile_viewed', {
      is_verified: isEmailVerified,
      has_username: hasUsername,
      profile_complete_pct: calculateProfileCompleteness(),
      has_profile_photo: !!currentUser?.photoURL
    });
  }, [trackPageView, trackCustomEvent, isEmailVerified, hasUsername, currentUser, calculateProfileCompleteness]);
    // Load profile data when available
  useEffect(() => {
    if (userProfile) {
      setIntroduction(userProfile.introduction || '');
      setConsentUnderstanding(userProfile.consentUnderstanding || ''); // Load consent understanding
      setInterests(userProfile.interests || []);
      setPhotoUrls(userProfile.photoUrls || []);
      setGender(userProfile.gender || '');
      setGenderOther(userProfile.genderOther || '');
      setSexuality(userProfile.sexuality || '');
      setSexualityOther(userProfile.sexualityOther || '');
      setBirthDate(userProfile.birthDate || '');      setUsername(userProfile.username || '');
      
      setOriginalIntroduction(userProfile.introduction || '');
      setOriginalConsentUnderstanding(userProfile.consentUnderstanding || ''); // Set original consent understanding
      setOriginalInterests(userProfile.interests || []);
      setOriginalPhotoUrls(userProfile.photoUrls || []);
      setOriginalGender(userProfile.gender || '');
      setOriginalGenderOther(userProfile.genderOther || '');
      setOriginalSexuality(userProfile.sexuality || '');
      setOriginalSexualityOther(userProfile.sexualityOther || '');
      setOriginalBirthDate(userProfile.birthDate || '');
      setOriginalUsername(userProfile.username || '');
      
      setHasChanges(false);
    }
  }, [userProfile]);
  // Check for changes
  useEffect(() => {
    const arraysEqual = (a: any[], b: any[]) => {
      if (a.length !== b.length) return false;
      return a.every((val, idx) => val === b[idx]);
    };
    
    const introductionChanged = introduction !== originalIntroduction;
    const consentUnderstandingChanged = consentUnderstanding !== originalConsentUnderstanding; // Add consent understanding check
    const interestsChanged = !arraysEqual(interests, originalInterests);
    const photosChanged = !arraysEqual(photoUrls, originalPhotoUrls);
    const hasNewPhoto = newPhoto !== null;
    const genderChanged = gender !== originalGender;
    const genderOtherChanged = genderOther !== originalGenderOther;
    const sexualityChanged = sexuality !== originalSexuality;
    const sexualityOtherChanged = sexualityOther !== originalSexualityOther;
    const usernameChanged = username !== originalUsername;
    // Only consider birthDate changes if it wasn't previously set
    const birthDateChanged = originalBirthDate === '' ? birthDate !== originalBirthDate : false;
      setHasChanges(introductionChanged || consentUnderstandingChanged || interestsChanged || photosChanged || hasNewPhoto || 
                 genderChanged || genderOtherChanged || sexualityChanged || sexualityOtherChanged || 
                 usernameChanged || birthDateChanged);
  }, [introduction, consentUnderstanding, interests, photoUrls, newPhoto, originalIntroduction, originalConsentUnderstanding, originalInterests, originalPhotoUrls,
    gender, genderOther, sexuality, sexualityOther, birthDate, username,
    originalGender, originalGenderOther, originalSexuality, originalSexualityOther, originalBirthDate, originalUsername]);
  // Redirect to sign-in if not authenticated
  useEffect(() => {
    // Don't redirect if still loading data
    if (loading || profileLoading) {
      return;
    }
    
    // Not logged in - go to sign in
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }
    
    // No username yet - go to choose username
    // This ensures we only redirect after profile data is fully loaded
    if (!loading && !profileLoading && currentUser && !hasUsername) {
      logger.info('👤 User has no username, redirecting to username selection');
      navigate('/choose-username');
    }
  }, [currentUser, loading, profileLoading, hasUsername, navigate]);

  const handleAvatarUpload = async (imageBlob: Blob) => {
    if (!currentUser) {
      setError('You must be logged in to update your profile picture');
      return;
    }
    setPageLoading(true);
    setError('');
    setSuccess('');
    
    // Track avatar upload attempt
    trackCustomEvent('profile_avatar_update_attempt', {
      has_previous_avatar: !!currentUser.photoURL
    });
    
    try {
      const avatarFile = new File([imageBlob], `avatar-${currentUser.uid}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      await ensureUserRole(currentUser.uid);
      logger.info('🚀 Uploading avatar to Firebase Storage');
      const downloadURL = await uploadAvatar(currentUser.uid, avatarFile);
      logger.success('✅ Avatar uploaded successfully');
      await updateUserProfile({ photoURL: downloadURL });
      await updateExtendedProfile({ profilePictureUrl: downloadURL });
      
      // Track successful avatar update
      trackCustomEvent('profile_avatar_updated', {
        had_previous_avatar: !!currentUser.photoURL,
        source: 'profile_page'
      });
      
      logger.success('✅ User profile updated with new avatar');
      setSuccess('Profile picture updated successfully');
      setShowAvatarEditor(false);
    } catch (err: any) {
      logger.error('❌ Error updating avatar:', err);
      
      // Track avatar update error
      trackCustomEvent('profile_avatar_error', {
        error_type: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });
      
      if (err.code === 'permission-denied') {
        setError('Permission denied. Please try logging out and logging back in.');
      } else {
        setError('Failed to update profile picture. Please try again.');
      }
    } finally {
      setPageLoading(false);
    }
  };

  const handleAddInterest = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!interestInput.trim()) return;
    if (!interests.includes(interestInput.trim())) {
      setInterests([...interests, interestInput.trim()]);
    }
    setInterestInput('');
  };
  
  const handleRemoveInterest = (interest: string) => {
    setInterests(interests.filter(item => item !== interest));
  };
  
  const handleNewPhotoChange = (file: File | null, previewUrl: string | null) => {
    setNewPhoto(file);
    setNewPhotoPreview(previewUrl);
    setError('');
  };

  const handlePhotoUpload = async (file: File) => {
    if (!currentUser) throw new Error('You must be logged in to upload photos');
    if (photoUrls.length >= 5) throw new Error('Maximum 5 photos allowed');
    
    // Track photo upload attempt
    trackCustomEvent('profile_photo_upload_attempt', {
      current_photo_count: photoUrls.length,
      file_size_kb: Math.round(file.size / 1024)
    });
    
    try {
      await ensureUserRole(currentUser.uid);
      const downloadURL = await uploadProfilePhoto(currentUser.uid, file);
      setPhotoUrls(prev => [...prev, downloadURL]);
      
      // Track successful photo upload
      trackCustomEvent('profile_photo_uploaded', {
        new_photo_count: photoUrls.length + 1,
        file_size_kb: Math.round(file.size / 1024)
      });
      setNewPhoto(null);
      setNewPhotoPreview(null);
      return downloadURL;
    } catch (err: any) {
      logger.error('❌ Error uploading photo:', err);
      if (err.code === 'permission-denied') {
        throw new Error('Permission denied. Please try logging out and logging back in.');
      }
      throw err;
    }
  };
  
  const handleRemovePhoto = async (url: string) => {
    try {
      // Track photo removal attempt
      trackCustomEvent('profile_photo_remove_attempt', {
        current_photo_count: photoUrls.length
      });
      
      if (url.includes('firebasestorage.googleapis.com')) {
        await deleteFileByUrl(url);
        logger.success('🗑️ Photo deleted from storage');
      }
      setPhotoUrls(photoUrls.filter(item => item !== url));
      
      // Track successful photo removal
      trackCustomEvent('profile_photo_removed', {
        new_photo_count: photoUrls.length - 1
      });
    } catch (err) {
      logger.error('❌ Error removing photo:', err);
      setError('Failed to remove photo. Please try again.');
      
      // Track error
      trackCustomEvent('profile_photo_remove_error', {
        error_message: err instanceof Error ? err.message : 'Unknown error'
      });
    }
  };
  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    
    // Track profile save attempt with field changes
    trackCustomEvent('profile_save_attempt', {
      introduction_changed: introduction !== originalIntroduction,
      consent_understanding_changed: consentUnderstanding !== originalConsentUnderstanding, // Track consent understanding changes
      interests_changed: JSON.stringify(interests) !== JSON.stringify(originalInterests),
      photos_changed: JSON.stringify(photoUrls) !== JSON.stringify(originalPhotoUrls),
      gender_changed: gender !== originalGender,
      sexuality_changed: sexuality !== originalSexuality,
      birthdate_changed: birthDate !== originalBirthDate,
      username_changed: username !== originalUsername,
      has_new_photo: !!newPhoto
    });
    
    
    if (photoUrls.length > 5) {
      setError('Maximum 5 photos allowed');
      
      // Track validation error
      trackCustomEvent('profile_validation_error', {
        error_type: 'too_many_photos'
      });
      return;
    }    
    // Validate username if it changed
    if (username !== originalUsername) {
      if (!username.trim()) {
        setError('Username is required');
        trackCustomEvent('profile_validation_error', {
          error_type: 'username_required'
        });
        return;
      }

      try {
        const validation = await validateUsername(username);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid username');
          trackCustomEvent('profile_validation_error', {
            error_type: 'username_invalid',
            error_message: validation.error
          });
          return;
        }
      } catch (err: any) {
        setError('Error validating username. Please try again.');
        trackCustomEvent('profile_validation_error', {
          error_type: 'username_validation_error',
          error_message: err.message
        });
        return;
      }
    }

    // Validate introduction length if provided
    if (introduction.trim() && introduction.trim().length < 50) {
      setError('Introduction must be at least 50 characters if provided');
      trackCustomEvent('profile_validation_error', {
        error_type: 'introduction_too_short'
      });
      return;
    }
    
    // Validate consent understanding length if provided
    if (consentUnderstanding.trim() && consentUnderstanding.trim().length < 50) {
      setError('Consent understanding must be at least 50 characters if provided');
      trackCustomEvent('profile_validation_error', {
        error_type: 'consent_understanding_too_short'
      });
      return;
    }
    
    setPageLoading(true);
    setError('');
    setSuccess('');
    try {      if (currentUser) await ensureUserRole(currentUser.uid);      // Handle username update first if it changed
      if (username !== originalUsername) {        try {
          await updateUsername(currentUser!, username);
          logger.success('✅ Username updated successfully');
        } catch (err: any) {
          logger.error('❌ Error updating username:', err);
          
          // Create user-friendly error message
          let errorMessage = 'Failed to update username. Please try again.';
          if (err.code === 'permission-denied' || (err.message && err.message.includes('PERMISSION_DENIED'))) {
            errorMessage = 'You don\'t have permission to change your username. Please try logging out and back in.';
          } else if (err.message && err.message.includes('Username is already taken')) {
            errorMessage = 'This username is already taken. Please choose another one.';
          }
          
          setError(errorMessage);
          trackCustomEvent('profile_update_error', {
            error_type: 'username_update_failed',
            error_message: err.message
          });
          setPageLoading(false);
          return;
        }
      }

      let currentPhotoUrls = [...photoUrls]; // Start with existing URLs
      if (newPhoto) { // If there's a new photo, upload it and add its URL
        const downloadURL = await uploadProfilePhoto(currentUser!.uid, newPhoto);
        currentPhotoUrls = [...currentPhotoUrls, downloadURL]; // Add new URL to the list
        setNewPhoto(null);
        setNewPhotoPreview(null);
      }
      
      // Prepare profile data with new fields
      const updatedProfileData: any = {
        introduction,
        consentUnderstanding, // Add consent understanding to profile data
        interests,
        photoUrls: currentPhotoUrls, // Use the potentially updated list
      };
      
      // Add gender data if present
      if (gender) {
        updatedProfileData.gender = gender;
        if (gender === 'Other' && genderOther) {
          updatedProfileData.genderOther = genderOther;
        } else {
          // Clear genderOther if gender is not "Other"
          updatedProfileData.genderOther = '';
        }
      }

      // Add sexuality if present
      if (sexuality) {
        updatedProfileData.sexuality = sexuality;
        if (sexuality === 'Other' && sexualityOther) {
          updatedProfileData.sexualityOther = sexualityOther;
        } else {
          // Clear sexualityOther if sexuality is not "Other"
          updatedProfileData.sexualityOther = '';
        }
      }
      
      // Add birthDate if present and not previously set
      if (birthDate && originalBirthDate === '') {
        updatedProfileData.birthDate = birthDate;
      }
      
      await updateExtendedProfile(updatedProfileData);
      
      // Update original state values
      setOriginalIntroduction(introduction);
      setOriginalConsentUnderstanding(consentUnderstanding); // Update original consent understanding
      setOriginalInterests([...interests]);
      setOriginalPhotoUrls([...currentPhotoUrls]); // Update original with current list
      setPhotoUrls([...currentPhotoUrls]); // Ensure state reflects the saved URLs
      setOriginalGender(gender);      
      setOriginalGenderOther(genderOther);
      setOriginalSexuality(sexuality);
      setOriginalSexualityOther(sexualityOther);
      setOriginalUsername(username);
      
      // Only update originalBirthDate if it wasn't previously set
      if (originalBirthDate === '' && birthDate) {
        setOriginalBirthDate(birthDate);
      }
      
      setHasChanges(false);
      setSuccess('Profile updated successfully');
      logger.success('✅ Extended profile updated');
      
      // Track successful profile update
      trackCustomEvent('profile_updated', {
        profile_complete_pct: calculateProfileCompleteness(),
        fields_updated: Object.keys(updatedProfileData).length + (username !== originalUsername ? 1 : 0),
        has_introduction: !!introduction,
        has_consent_understanding: !!consentUnderstanding, // Track presence of consent understanding
        interests_count: interests.length,
        photos_count: currentPhotoUrls.length,
        has_gender: !!gender,
        has_sexuality: !!sexuality,        has_birthdate: !!birthDate,
        username_updated: username !== originalUsername
      });
    } catch (err: any) {
      logger.error('❌ Error saving profile:', err);
      
      // Track profile update error
      trackCustomEvent('profile_update_error', {
        error_code: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });      
      if (err.code === 'permission-denied') {
        setError('Permission denied. Please try logging out and logging back in.');
      } else if (err.message && err.message.includes('PERMISSION_DENIED')) {
        // Cleaner error message for Firebase permission issues
        setError('You don\'t have permission to make this change. Please try logging out and back in.');
      } else {
        setError('Failed to update profile. Please try again.');
      }    } finally {
      setPageLoading(false);
    }
  };
  
  // Handle sending verification email
  const handleSendVerification = async () => {
    if (verificationCountdown > 0) {
      return;
    }
    
    setPageLoading(true);
    setError(''); // Clear any existing errors
    
    // Track verification email request
    trackCustomEvent('verification_email_requested', {
      source: 'profile_page',
      is_resend: verificationSent // Track if this is a resend
    });
    
    try {
      await verifyEmail();
      // Only set one of these to prevent duplicate messages
      setSuccess('');  // Clear any existing success message
      setVerificationSent(true);
      setVerificationCountdown(60); // Start countdown from 60 seconds
      
      // Store timestamp in localStorage for consistent experience across pages
      localStorage.setItem('verificationEmailSent', Date.now().toString());
      
      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setVerificationCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      logger.info('📧 Verification email sent');      
      // Set appropriate success message
      const successMessage = verificationSent 
        ? 'Another verification email has been sent. Please check your inbox or spam folder. You can send another email in 60 seconds.' 
        : 'A verification email has been sent. Please check your inbox or spam folder. You can send another email in 60 seconds.';
      
      setSuccess(successMessage);
      
      // Track success
      trackCustomEvent('verification_email_sent', {
        source: 'profile_page',
        is_resend: verificationSent
      });
    } catch (err) {
      logger.error('❌ Error sending verification email', err);
      setError('Failed to send verification email. Please try again later.');
      
      // Track error
      trackCustomEvent('verification_email_error', {
        error_message: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setPageLoading(false);
    }
  };
  // Check localStorage for existing countdown on component mount
  useEffect(() => {
    if (!currentUser) return;
    
    const emailSentTimestamp = localStorage.getItem('verificationEmailSent');
    if (emailSentTimestamp && !isEmailVerified) {
      const elapsed = Math.floor((Date.now() - parseInt(emailSentTimestamp)) / 1000);
      const cooldown = 60; // 60 seconds cooldown
      
      if (elapsed < cooldown) {
        setVerificationSent(true);
        setVerificationCountdown(cooldown - elapsed);
        
        // Start countdown timer
        const countdownInterval = setInterval(() => {
          setVerificationCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              setVerificationSent(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        return () => clearInterval(countdownInterval);
      }
    }
  }, [currentUser, isEmailVerified]);// Add useEffect to check for email verification status on load and periodically
  useEffect(() => {
    if (!currentUser) return;
      // Start watching for verification status changes
    const stopWatching = watchEmailVerificationStatus((verified) => {
      if (verified) {
        // User's email was just verified
        setVerificationSent(false);
        setVerificationCountdown(0);
        setSuccess('Your email has been verified successfully! You can now apply for events.');
        
        // Remove verification email timestamp from localStorage
        localStorage.removeItem('verificationEmailSent');
        
        // Force an immediate UI update to reflect verified status
        logger.info('✅ Email verified - updating UI immediately');
        
        // Force a user refresh and trigger a complete re-render
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (user) {          // Force reload the user object to get the latest verification status
          user.reload()
            .then(() => {
              // Force a re-render by updating the auth state
              const updatedUser = auth.currentUser;
              if (updatedUser) {
                // Double check that the emailVerified flag is set
                if (updatedUser.emailVerified) {
                  logger.info('✅ Confirmed email is verified on reloaded user object');
                }
                
                // This is a hack to force the auth state to update
                // by triggering the onAuthStateChanged listener
                auth.updateCurrentUser(updatedUser)
                  .then(() => {
                    logger.info('✅ Auth state updated with verified status');
                    
                    // Broadcast verification status update to other open tabs
                    try {
                      localStorage.setItem('emailVerificationComplete', Date.now().toString());
                      // Force a page reload if the UI doesn't update correctly
                      setTimeout(() => {
                        // Only reload if the yellow box is still visible despite verification
                        const verificationBox = document.querySelector('.verification-status:not(.verified)');
                        if (verificationBox && updatedUser.emailVerified) {
                          logger.warn('⚠️ UI did not update properly, forcing page reload');
                          window.location.reload();
                        }
                      }, 2000); // Give the UI a chance to update first
                    } catch (err) {
                      logger.error('❌ Error updating localStorage', err);
                    }
                  })
                  .catch((err: Error) => {
                    logger.error('❌ Error updating auth state', err);
                  });
              }
              logger.info('🔄 Reloaded user data to update verification status');
            })
            .catch((err: Error) => {
              logger.error('❌ Error reloading user data', err);
            });
        }
        
        // Track this event
        trackCustomEvent('email_verified', {
          source: 'profile_page'
        });
        
        logger.info('✅ Email has been verified');
      }
    });
    
    return () => {
      stopWatching();
    };
  }, [currentUser, watchEmailVerificationStatus, trackCustomEvent, isEmailVerified]);  // Listen for verification status updates from other tabs  // Listen for verification status updates from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'emailVerificationComplete' && currentUser) {
        // Another tab completed verification
        logger.info('🔄 Detected email verification from another tab');
        
        // Force refresh the current user to get the latest verification status
        currentUser.reload()
          .then(() => {
            const auth = getAuth();
            // This is a hack to force the auth state to update
            if (auth.currentUser) {
              // If the user's email is now verified, update UI
              if (auth.currentUser.emailVerified) {
                logger.info('✅ User email is now verified from cross-tab notification');
                
                // Set success message
                setSuccess('✅ Your email has been verified successfully! You can now apply for events. This message will disappear in 30 seconds.');
                
                // Reset verification state
                setVerificationSent(false);
                setVerificationCountdown(0);
                
                // Track this event
                trackCustomEvent('email_verified_cross_tab', {
                  source: 'storage_event'
                });
              }
              
              auth.updateCurrentUser(auth.currentUser)
                .then(() => {
                  logger.info('✅ Auth state updated from localStorage change');
                })
                .catch((err: Error) => {
                  logger.error('❌ Error updating auth state from storage change', err);
                });
            }
          })
          .catch((err: Error) => {
            logger.error('❌ Error reloading user in storage listener', err);
          });
      }
    };    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUser, isEmailVerified, trackCustomEvent]);
  // Show success message if redirected after successful verification
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verified = params.get('verified');
    
    if (verified === 'true' && isEmailVerified) {
      setSuccess('Your email has been verified successfully!');
      
      // Track this event
      trackCustomEvent('email_verification_callback', {
        source: 'callback_redirect',
        status: 'success'
      });
    }
  }, [isEmailVerified, trackCustomEvent]);
  // Handle account deletion confirmation
  const handleDeleteAccountConfirm = async () => {
    // Track account deletion attempt
    trackCustomEvent('account_deletion_attempt', {
      account_age_days: currentUser ? 
        Math.floor((Date.now() - new Date(currentUser.metadata.creationTime || Date.now()).getTime()) / (1000 * 60 * 60 * 24)) : 
        0,
      profile_complete_pct: calculateProfileCompleteness()
    });
    
    setIsDeleting(true);
    setDeleteError('');

    if (!currentUser) {
      setDeleteError('No user logged in. Please refresh and try again.');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      
      // Track error
      trackCustomEvent('account_deletion_error', {
        error_type: 'no_user_logged_in'
      });
      return;
    }

    try {
      logger.info(`🚀 Requesting account deletion for UID: ${currentUser.uid}`);
      await requestDeleteUserAccount({ uid: currentUser.uid });
      logger.success(`✅ Account deletion request successful for UID: ${currentUser.uid}`);
      
      // Track successful account deletion request
      trackCustomEvent('account_deleted', {
        account_age_days: Math.floor((Date.now() - new Date(currentUser.metadata.creationTime || Date.now()).getTime()) / (1000 * 60 * 60 * 24)),
        profile_complete_pct: calculateProfileCompleteness(),
        had_verified_email: isEmailVerified,
        had_profile_photo: !!currentUser.photoURL
      });
      
      await signOut();
      logger.info('🔒 User signed out after account deletion');
      navigate('/'); // Navigate to homepage or sign-in page
      
    } catch (err: any) {
      logger.error(`❌ Error deleting account for UID: ${currentUser.uid}`, err);
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
      
      // Track account deletion error
      trackCustomEvent('account_deletion_error', {
        error_code: err.code || 'unknown',
        error_message: err.message || 'Unknown error'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false); // Close dialog regardless of outcome
    }
  };


  // Check if verification email was already sent previously
  useEffect(() => {
    const emailSentTimestamp = localStorage.getItem('verificationEmailSent');
    
    if (emailSentTimestamp) {
      const sentTime = parseInt(emailSentTimestamp);
      const elapsed = Math.floor((Date.now() - sentTime) / 1000);
      const cooldown = 60; // 60 seconds cooldown
      
      if (elapsed < cooldown) {
        setVerificationSent(true);
        setVerificationCountdown(cooldown - elapsed);
        
        // Set up countdown timer
        const timer = setInterval(() => {
          setVerificationCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        return () => clearInterval(timer);
      } else if (currentUser && !currentUser.emailVerified) {
        // Email was sent, but it was more than 60 seconds ago and still not verified
        setVerificationSent(true);
        setVerificationCountdown(0);
      }
    }
  }, [currentUser]);
  // Auto-dismiss success messages after 30 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
        // Use different log message based on whether it was a verification message
        if (success.includes('verified') && isEmailVerified) {
          logger.info('🔄 Auto-cleared verification success message');
        } else {
          logger.debug('� Auto-dismissed success message after timeout');
        }
      }, 30000); // 30 seconds
      
      return () => clearTimeout(timer);
    }
  }, [success, isEmailVerified]);

  if (loading || profileLoading) {
    return <div className="loading-indicator">Loading profile...</div>;
  }

  if (!currentUser || !userProfile) {
    return null; // Will be redirected by useEffect
  }

  return (
    <>
      <PageHeader
        title="My Profile"
        subtitle="Show the community who you are"
      />
      <div className="profile-container">
        <h2>My Profile</h2>
        {error && <div className="auth-error">{error}</div>}
        {/* Only show success messages if they're not about verification or if email isn't already verified */}
        {success && !isEmailVerified && <div className="auth-success">{success}</div>}
        {/* Only show verification sent message if email is not verified */}
        {verificationSent && !success && !isEmailVerified && (
          <div className="auth-success">
            Verification email sent! Please check your inbox/spam inbox.
          </div>
        )}

        <div className="profile-header">
          {showAvatarEditor ? (
            <AvatarEditor 
              initialImage={currentUser.photoURL}
              onSave={handleAvatarUpload}
              onCancel={() => setShowAvatarEditor(false)}
            />
          ) : (
            <>
              <div 
                className="profile-avatar-container"
                onClick={() => setShowAvatarEditor(true)}
                title="Click to change profile picture"
              >
                {currentUser.photoURL ? (
                  <img 
                    src={currentUser.photoURL} 
                    alt="Profile" 
                    className="profile-avatar"
                  />
                ) : (                  <div className="profile-avatar-placeholder">
                    {userProfile.username
                      ? userProfile.username.charAt(0).toUpperCase()
                      : userProfile.firstName?.charAt(0).toUpperCase() || currentUser.email?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="avatar-edit-overlay">
                  <FontAwesomeIcon icon={faCamera} />
                </div>
              </div>                <div className="profile-header-info">
                <h3>@{userProfile.username}</h3>
                <div className="profile-email-container">
                  <p className="profile-email">{currentUser.email}</p>
                  {isEmailVerified && (
                    <button className="email-verified-button" disabled>
                      ✓ Verified
                    </button>
                  )}
                </div>
                
                {userProfile.membershipStatus && (
                  <span className={`membership-badge membership-${userProfile.membershipStatus.toLowerCase()}`}>
                    {userProfile.membershipStatus}
                  </span>
                )}
              </div>
            </>
          )}        </div>
        
        {/* Display verification section with current status */}
        {!isEmailVerified && (
          <div className="verification-status">
            <div className="verification-content">
              <span className="unverified-badge">Unverified Email</span>
              <p className="verification-message-text">
                You need to verify your email address before you can apply for events.
                {verificationSent ? 
                  ' A verification email has already been sent, please check your inbox or your spam folder.' : 
                  ' Click the button below to send a verification email.'}
              </p>
            </div>
            
            <button
              onClick={handleSendVerification}
              disabled={pageLoading || verificationCountdown > 0}
              className="verify-button"
            >
              {pageLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="icon-left" /> Sending...
                </>
              ) : verificationCountdown > 0 ? (
                `Resend in ${verificationCountdown}s`
              ) : verificationSent ? (
                'Resend Verification Email'
              ) : (
                'Send Verification Email'
              )}
            </button>
          </div>
        )}
        
        
        <div className="auth-section-divider"></div>
        
        <form onSubmit={handleSaveProfile} className="profile-form">          <div className="form-group">
            <label htmlFor="username">Public Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your public username"
              disabled={pageLoading}
              required
              minLength={3}
              maxLength={20}
            />           
          </div>            <div className="form-group">
              <label htmlFor="introduction">Introduction (Optional)</label>
              <textarea
                id="introduction"
                value={introduction}
                onChange={(e) => setIntroduction(e.target.value)}
                rows={4}
                placeholder="Tell the community about yourself:
                • Where are you from?
                • Why do you want to join our community?
                • What are your kinks and interests?"
                disabled={pageLoading}
              />              <div className="char-counter">
                {introduction.length} / 50 characters minimum
                {introduction.length < 50 && introduction.length > 0 && (
                  <span className="char-counter-warning"></span>
                )}
                {introduction.length >= 50 && (
                  <span className="char-counter-success"> ✓</span>
                )}
              </div>
              <small className="form-hint">This information may be used to auto-fill your event applications. If provided, must be at least 50 characters.</small>
            </div>            <div className="form-group">
              <label htmlFor="consentUnderstanding">Consent Understanding (Optional)</label>
              <textarea
                id="consentUnderstanding"
                value={consentUnderstanding}
                onChange={(e) => setConsentUnderstanding(e.target.value)}
                rows={4}
                placeholder="In your own words, please explain what consent means to you and how it applies to intimate settings..."
                disabled={pageLoading}
              />              <div className="char-counter">
                {consentUnderstanding.length} / 50 characters minimum
                {consentUnderstanding.length < 50 && consentUnderstanding.length > 0 && (
                  <span className="char-counter-warning"></span>
                )}
                {consentUnderstanding.length >= 50 && (
                  <span className="char-counter-success"> ✓</span>
                )}
              </div>
              <small className="form-hint">This information may be used to auto-fill your event applications. If provided, must be at least 50 characters.</small>
            </div>
          
          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as GenderOption)}
              disabled={pageLoading}
              className="auth-select"
            >
              <option value="" disabled>Select your gender</option>
              {genderOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {gender === 'Other' && (
            <div className="form-group">
              <label htmlFor="genderOther">Please specify</label>
              <input
                type="text"
                id="genderOther"
                value={genderOther}
                onChange={(e) => setGenderOther(e.target.value)}
                placeholder="Specify your gender"
                disabled={pageLoading}
                maxLength={50}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="sexuality">Sexuality</label>
            <select
              id="sexuality"
              value={sexuality}
              onChange={(e) => setSexuality(e.target.value as SexualityOption)}
              disabled={pageLoading}
              className="auth-select"
            >
              <option value="" disabled>Select your sexuality</option>
              {sexualityOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {sexuality === 'Other' && (
            <div className="form-group">
              <label htmlFor="sexualityOther">Please specify</label>
              <input
                type="text"
                id="sexualityOther"
                value={sexualityOther}
                onChange={(e) => setSexualityOther(e.target.value)}
                placeholder="Specify your sexuality"
                disabled={pageLoading}
                maxLength={50}
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="birthDate">Date of Birth</label>
            <input
              type="date"
              id="birthDate"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              disabled={pageLoading || (originalBirthDate !== '')}
            />
            {originalBirthDate !== '' && (
              <small className="form-hint warning">
                Date of birth cannot be changed once set.
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="interests">Interests</label>
            <div className="interests-input-container">
              <input
                type="text"
                id="interests"
                value={interestInput}
                onChange={(e) => setInterestInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddInterest(e)}
                placeholder="Add your interests (press Enter)"
                disabled={pageLoading}
              />
              <button 
                type="button" 
                onClick={handleAddInterest} 
                className="add-interest-button"
                disabled={!interestInput.trim() || pageLoading}
              >
                Add
              </button>
            </div>
            <div className="interests-container">
              {interests.map(interest => (
                <span key={interest} className="interest-tag">
                  {interest}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveInterest(interest)}
                    className="remove-tag"
                    aria-label={`Remove ${interest}`}
                    disabled={pageLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="photos">Photos (max 5)</label>
            {photoUrls.length < 5 && (
              <div className="photo-upload-section">
                <PhotoUpload
                  value={newPhoto}
                  previewUrl={newPhotoPreview}
                  onChange={handleNewPhotoChange}
                  onUpload={handlePhotoUpload} // This should be the function that takes a file and uploads
                  disabled={pageLoading || photoUrls.length >= 5}
                  maxSizeMB={5}
                  instantUpload={false} // Set to false to allow save button to trigger final upload
                  label="Select Photo"
                  helperText="Select an image to add to your profile (max 5MB). Photo will be uploaded on save."
                  className="profile-photo-upload"
                />
              </div>
            )}
            <small className="form-hint">
              {photoUrls.length}/5 photos added
            </small>
            <div className="photos-grid">
              {photoUrls.map((url, index) => (
                <div key={index} className="photo-item">
                  <img src={url} alt={`User uploaded ${index + 1}`} />
                  <button 
                    type="button" 
                    onClick={() => handleRemovePhoto(url)}
                    className="remove-photo"
                    aria-label="Remove photo"
                    disabled={pageLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="profile-actions">
            <button
              type="submit"
              className={`save-button ${hasChanges ? 'save-button-cta' : ''}`}
              disabled={pageLoading || !hasChanges || isDeleting}
            >
              {pageLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} /> Save Profile
                </>
              )}
            </button>
          </div>
        </form>

        <div className="auth-section-divider"></div>

        <div className="account-danger-zone">
          <h4>Danger Zone</h4>
          <p>Manage sensitive account settings.</p>
          {deleteError && <div className="auth-error delete-error-message">{deleteError}</div>}
          <button
            type="button"
            className="delete-account-button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting || pageLoading}
          >
            {isDeleting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Deleting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} /> Delete My Account
              </>
            )}
          </button>
        </div>

      </div>

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Account?"
          message="Are you sure you want to permanently delete your account? This action cannot be undone, and all your data will be removed."
          confirmLabel={isDeleting ? "Deleting..." : "Delete My Account"}
          cancelLabel="Cancel"
          isDestructive={true}
          onConfirm={handleDeleteAccountConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting} // Pass isDeleting to control button state in dialog
        />
      )}
    </>
  );
};

export default UserProfile;
